import React from 'react';
import { Select } from 'antd';
import moment from 'moment-timezone';
import SelectIcon from 'assets/svg/Header/SelectIcon';
import { LOCAL_STORAGE_VAR } from 'constants/Common';
import UtilService from 'services/util';
import { GET_COUNTRY_LIST } from 'constants/apiConstant';
import { EventManager } from 'services/eventBus';

const { Option } = Select;

const baseTimezoneMapping = {
  'Asia/Kolkata': 'India',
  'Asia/Hong_Kong': 'Hong Kong',
  'Europe/Brussels': 'Belgium',
  'America/New_York': 'USA',
  'Asia/Dubai': 'Dubai',
};

const resolveTimezoneFromCountry = (country) => {
  if (!country?.shortCode) return null;
  try {
    const zones = moment.tz.zonesForCountry(country?.shortCode?.toUpperCase());
    if (!zones?.length) return null;

    // Check if any of this country's timezones already exist in base mapping
    const existingZone = zones?.find((z) => baseTimezoneMapping[z]);
    if (existingZone) {
      return { timezone: existingZone, countryName: baseTimezoneMapping[existingZone], alreadyInBase: true };
    }

    // If browser's guessed timezone matches one of the country's zones, prefer it
    const guessed = moment.tz.guess();
    const timezone = zones?.includes(guessed) ? guessed : zones[0];

    return { timezone, countryName: country?.name };
  } catch (e) {
    return null;
  }
};

const getDefaultTimezone = () => {
  const guessed = moment.tz.guess();
  return Object.keys(baseTimezoneMapping).includes(guessed) ? guessed : 'Asia/Kolkata';
};

const TimeZoneDropdown = () => {
  const [timezoneMapping, setTimezoneMapping] = React.useState(baseTimezoneMapping);
  const [selectedTimezone, setSelectedTimezone] = React.useState(getDefaultTimezone);
  const [currentTime, setCurrentTime] = React.useState(() => moment().tz(getDefaultTimezone()));

  const applyUserCountry = React.useCallback((country) => {
    const info = resolveTimezoneFromCountry(country);
    if (!info) return;

    // Reset to base mapping + user's country (removes previously added dynamic country)
    const mapping = { ...baseTimezoneMapping };
    if (!mapping[info?.timezone]) {
      mapping[info.timezone] = info?.countryName;
    }
    setTimezoneMapping(mapping);
    setSelectedTimezone(info?.timezone);
  }, []);

  React.useEffect(() => {
    try {
      const user = UtilService.getLocalStorageItem(`${LOCAL_STORAGE_VAR}-user`);
      if (!user) return;

      const country = user?.account?.country || user?.country;
      if (!country) return;

      // If country is a populated object with name and shortCode
      if (typeof country === 'object' && country?.name && country?.shortCode) {
        applyUserCountry(country);
        return;
      }

      // If country is just an ID string, fetch country list to look it up
      const countryId = typeof country === 'object' ? country.id : country;
      if (!countryId) return;

      UtilService.callApi({ ...GET_COUNTRY_LIST }, (err, data) => {
        if (data?.code === 'OK' && data.data) {
          const found = data?.data?.find((c) => c?.id === countryId);
          if (found) {
            applyUserCountry(found);
          }
        }
      });
    } catch (e) {
      // silently fail
    }
  }, [applyUserCountry]);

  // Listen for country change from Company Information save
  React.useEffect(() => {
    const unlisten = EventManager.listen('@account/countryChanged', (country) => {
      applyUserCountry(country);
    });
    return unlisten;
  }, [applyUserCountry]);

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(moment().tz(selectedTimezone));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [selectedTimezone]);

  const timezones = Object.keys(timezoneMapping);

  return (
    <>
      <Select
        value={selectedTimezone}
        style={{ width: 150 }}
        onChange={setSelectedTimezone}
        suffixIcon={<SelectIcon />}
      >
        {timezones.map((timezone) => (
          <Option key={timezone} value={timezone}>
            {timezoneMapping[timezone]}
          </Option>
        ))}
      </Select>
      <p>{currentTime?.format('DD MMM YYYY, h:mm a (z)')}</p>
    </>
  );
};

export default TimeZoneDropdown;
